import React, { useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import BaseModal from 'flow/components/ui-components/BaseModal';
import Icon from 'flow/components/ui-components/Icon';
import SearchInput from 'flow/components/ui-components/SearchInput';
import Skeleton from 'flow/components/ui-components/Skeleton';
import { useModal } from 'flow/contexts/ModalProvider';
import {
    usePremadeAnswers,
    useUpdatePremadeAnswer,
} from 'flow/hooks/api/usePremadeAnswer';
import useAppContext from 'flow/hooks/useAppContext';
import { PremadeAnswer } from 'flow/types/Chats';
import { TicketKey } from 'flow/types/EntityKeys';
import { twMerge } from 'tailwind-merge';

interface Props {
    ticketKey?: TicketKey;
    onSelectAnswer?: (answer: PremadeAnswer) => void;
}

const PremadeAnswerTicketModal = ({ onSelectAnswer }: Props) => {
    const { i18next } = useAppContext();
    const { openModal, closeModal } = useModal();
    const { data: premadeAnswers, isLoading } = usePremadeAnswers();
    const updatePremadeAnswer = useUpdatePremadeAnswer();

    const [search, setSearch] = useState('');
    const [selectedKey, setSelectedKey] = useState<
        PremadeAnswer['key'] | undefined
    >(undefined);

    const filtered = useMemo(
        () =>
            premadeAnswers?.filter(
                (pa) =>
                    pa.type === 'EMAIL_SIGNATURE' &&
                    pa.name.toLowerCase().includes(search.toLowerCase()),
            ) ?? [],
        [premadeAnswers, search],
    );

    const selected = useMemo(
        () => filtered.find((pa) => pa.key === selectedKey),
        [filtered, selectedKey],
    );

    const handleInsert = () => {
        if (selected && onSelectAnswer) {
            onSelectAnswer(selected);
            closeModal();
        }
    };

    const handleSetDefault = (pa: PremadeAnswer) => {
        updatePremadeAnswer.mutate({
            key: pa.key,
            name: pa.name,
            message: pa.message,
            isDefault: !pa.isDefault,
            type: pa.type,
        });
    };

    return (
        <BaseModal
            open={true}
            header={
                <span className="text-lg text-gray-800">
                    {i18next.t('premadeAnswer.emailSignature')}
                </span>
            }
            disableSubmit={!selected}
            disableHeaderForcedLowerCase={true}
            onSubmit={handleInsert}
            confirmButtonText={i18next.t('premadeAnswer.insert')}
            className="min-h-[564px] w-[480px]"
        >
            <div className="flex grow flex-col gap-4">
                <div className="flex gap-2">
                    <SearchInput
                        className="flex-1"
                        iconKey="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoFocus={true}
                    />
                    <button
                        className="flex items-center gap-1 rounded border px-3 py-1.5 text-sm hover:bg-gray-100"
                        onClick={() =>
                            // Push the editor on top of this picker; closing it
                            // pops back here via previousModal (no reopen needed).
                            openModal('premadeAnswer', {
                                type: 'EMAIL_SIGNATURE',
                            })
                        }
                    >
                        <Icon iconKey="add" size="1rem" />
                        {i18next.t('premadeAnswer.create')}
                    </button>
                </div>

                <div className="flex flex-1 flex-col gap-1 overflow-y-auto">
                    {isLoading && (
                        <>
                            <SkeletonRow />
                            <SkeletonRow />
                            <SkeletonRow />
                        </>
                    )}
                    {!isLoading && filtered.length === 0 && (
                        <p className="py-8 text-center text-sm text-gray-400 italic">
                            {search
                                ? i18next.t(
                                      'premadeAnswer.noResultsEmailSignatures',
                                  )
                                : i18next.t(
                                      'premadeAnswer.emptyEmailSignatures',
                                  )}
                        </p>
                    )}
                    {!isLoading &&
                        filtered.length > 0 &&
                        [...filtered]
                            .sort((a, b) => {
                                if (a.isDefault && !b.isDefault) return -1;
                                if (!a.isDefault && b.isDefault) return 1;
                                return a.name.localeCompare(b.name);
                            })
                            .map((pa) => (
                                // The row itself is not a control: the select
                                // button and the edit / set-default buttons
                                // sit side by side, so no button nests another.
                                <div
                                    key={pa.key}
                                    className={twMerge(
                                        'group flex w-full items-center justify-between rounded p-2 text-start hover:bg-gray-100',
                                        selectedKey === pa.key &&
                                            'bg-gray-100 ring-1 ring-gray-300',
                                    )}
                                >
                                    <button
                                        type="button"
                                        aria-pressed={selectedKey === pa.key}
                                        className="flex min-w-0 flex-1 flex-col text-start"
                                        onClick={() => setSelectedKey(pa.key)}
                                        onDoubleClick={() => {
                                            setSelectedKey(pa.key);
                                            if (onSelectAnswer) {
                                                onSelectAnswer(pa);
                                                closeModal();
                                            }
                                        }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-gray-800">
                                                {pa.name}
                                            </span>
                                            {pa.isDefault && (
                                                <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700">
                                                    {i18next.t(
                                                        'premadeAnswer.default',
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                        <p className="w-full truncate text-xs text-gray-500">
                                            {pa.message.replace(/<[^>]*>/g, '')}
                                        </p>
                                    </button>
                                    <div className="sr-only-until-hover-or-focus flex gap-1">
                                        <button
                                            className="rounded p-1 hover:bg-gray-200"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // Editor pushes on top of this
                                                // picker; closing pops back here.
                                                openModal('premadeAnswer', {
                                                    premadeAnswer: pa,
                                                    type: 'EMAIL_SIGNATURE',
                                                });
                                            }}
                                            title={i18next.t(
                                                'premadeAnswer.edit',
                                            )}
                                        >
                                            <Icon iconKey="edit" size="1rem" />
                                        </button>
                                        <button
                                            className="rounded p-1 hover:bg-gray-200"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSetDefault(pa);
                                            }}
                                            title={
                                                pa.isDefault
                                                    ? i18next.t(
                                                          'premadeAnswer.unsetDefault',
                                                      )
                                                    : i18next.t(
                                                          'premadeAnswer.setDefault',
                                                      )
                                            }
                                        >
                                            <Icon
                                                iconKey={
                                                    pa.isDefault
                                                        ? 'star'
                                                        : 'favorite_outline'
                                                }
                                                size="1rem"
                                            />
                                        </button>
                                    </div>
                                </div>
                            ))}
                </div>

                {selected && (
                    <div className="flex flex-col gap-1.5">
                        <div className="text-xs font-bold">
                            {i18next.t('preview')}
                        </div>
                        <div className="scrollbar max-h-[184px] overflow-y-auto rounded bg-gray-100">
                            <div
                                className="p-3 text-sm"
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                        selected.message,
                                    ),
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
        </BaseModal>
    );
};

export default React.memo(PremadeAnswerTicketModal);

const SkeletonRow = () => (
    <div className="flex h-14 items-center rounded bg-gray-50 px-3 text-gray-200">
        <div className="flex flex-col gap-1">
            <Skeleton.Text className="h-4 w-32" />
            <Skeleton.Text className="h-3 w-48" />
        </div>
    </div>
);
